import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import { startOfMonth, endOfMonth, format } from "date-fns";

/**
 * Total expenses from log book entries (entry_type = 'expense') for the current month.
 * Only relevant when business has log_book_enabled.
 */
export function useLogBookExpenses() {
  const { business } = useBusiness();
  const [totalExpensesThisMonth, setTotalExpensesThisMonth] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!business?.id) {
      setLoading(false);
      return;
    }

    let mounted = true;

    const run = async () => {
      setLoading(true);
      try {
        const now = new Date();
        const start = format(startOfMonth(now), "yyyy-MM-dd");
        const end = format(endOfMonth(now), "yyyy-MM-dd");

        const { data, error } = await supabase
          .from("log_book_entries")
          .select("amount")
          .eq("business_id", business.id)
          .eq("entry_type", "expense")
          .gte("entry_date", start)
          .lte("entry_date", end);

        if (!mounted) return;
        if (error) throw error;

        const total = (data || []).reduce((sum, row) => sum + Number(row?.amount ?? 0), 0);
        setTotalExpensesThisMonth(total);
      } catch {
        if (mounted) setTotalExpensesThisMonth(0);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [business?.id]);

  return { totalExpensesThisMonth, loading };
}
